import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import ContactService from '../../services/contact.service';
import CustomerQuickSearch from '../../components/common/CustomerQuickSearch';
import { requestService } from '../../services/requestService';
import { normalizePhoneDigits } from '../../utils/customerSearch';

const ContactList: React.FC = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [serviceNumber, setServiceNumber] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [requestMatchPhones, setRequestMatchPhones] = useState<string[]>([]);

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    const term = serviceNumber.trim();
    if (term.length < 2) {
      setRequestMatchPhones([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const response = await requestService.getAll({
          'filters[requestNumber][$containsi]': term,
          'pagination[pageSize]': 50,
          sort: 'createdAt:desc',
        });
        const phones = (response.data || [])
          .map((req: any) => normalizePhoneDigits((req.attributes || req).phoneNumber))
          .filter(Boolean);
        setRequestMatchPhones(phones);
      } catch {
        setRequestMatchPhones([]);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [serviceNumber]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await ContactService.getAll({
        populate: '*',
        'pagination[pageSize]': 100,
        sort: 'createdAt:desc',
      });
      setContacts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = useMemo(() => {
    const mobileDigits = normalizePhoneDigits(mobileNumber);
    const servicePhones = new Set(requestMatchPhones);
    const hasService = serviceNumber.trim().length > 0;
    const hasMobile = mobileDigits.length > 0;

    if (!hasService && !hasMobile) return contacts;

    return contacts.filter((contact) => {
      const attrs = contact.attributes || contact;
      const phone = attrs.phone || attrs.whatsappNumber || '';
      const phoneDigits = normalizePhoneDigits(phone);

      if (hasMobile && phoneDigits.includes(mobileDigits)) return true;
      if (hasService && servicePhones.size > 0 && servicePhones.has(phoneDigits)) return true;
      return false;
    });
  }, [contacts, mobileNumber, serviceNumber, requestMatchPhones]);

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await ContactService.delete(id);
        fetchContacts();
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    }
  };

  const getInitials = (firstName: string, lastName?: string) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const getSourceColor = (source: string) => {
    const colors: any = {
      manual: 'default',
      import: 'info',
      whatsapp: 'success',
      instagram: 'secondary',
      website: 'primary',
      other: 'default',
    };
    return colors[source] || 'default';
  };

  const clearQuickSearch = () => {
    setServiceNumber('');
    setMobileNumber('');
  };

  const actionButtonSx = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1.5,
    mx: 0.25,
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" fontWeight={700}>Contacts</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/contact-groups')}
            sx={{ borderRadius: 2 }}
          >
            Manage Groups
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/contacts/create')}
            sx={{ borderRadius: 2, px: 2 }}
          >
            New Contact
          </Button>
        </Stack>
      </Stack>

      <CustomerQuickSearch
        title="Find Contact"
        serviceNumber={serviceNumber}
        mobileNumber={mobileNumber}
        onServiceNumberChange={setServiceNumber}
        onMobileNumberChange={setMobileNumber}
        onClear={clearQuickSearch}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Contact', 'Phone', 'Email', 'Company', 'Source', 'Social', 'Status'].map((label) => (
                  <TableCell
                    key={label}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      bgcolor: 'grey.50',
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredContacts.map((contact) => (
                <TableRow key={contact.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                        {getInitials(
                          contact.attributes?.firstName,
                          contact.attributes?.lastName
                        )}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {contact.attributes?.firstName} {contact.attributes?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {contact.attributes?.position || '-'}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {contact.attributes?.phone || contact.attributes?.whatsappNumber || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {contact.attributes?.email ? (
                      <Typography variant="body2">{contact.attributes?.email}</Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{contact.attributes?.company || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={contact.attributes?.source}
                      size="small"
                      color={getSourceColor(contact.attributes?.source)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {contact.attributes?.whatsappNumber && (
                        <Tooltip title="Open WhatsApp chat">
                          <IconButton
                            size="small"
                            color="success"
                            sx={actionButtonSx}
                            onClick={() =>
                              navigate(`/operations/whatsapp-chat/${contact.id}`)
                            }
                          >
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {contact.attributes?.instagramHandle && (
                        <IconButton size="small" color="secondary" sx={actionButtonSx}>
                          <InstagramIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={contact.attributes?.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={contact.attributes?.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        sx={actionButtonSx}
                        onClick={() => navigate(`/contacts/${contact.id}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        sx={actionButtonSx}
                        onClick={() => handleDelete(contact.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && filteredContacts.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 2, borderRadius: 2 }} elevation={0}>
          <Typography variant="body1" color="text.secondary">
            No contacts found
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ContactList;
